/**
 * MechaGuard - ParticleSystem (粒子系统)
 *
 * 使用对象池复用粒子实例：击杀爆炸、枪口火花、命中碎屑等高频特效
 * 每帧可能创建上百个对象，直接 new/GC 会造成明显卡顿。
 */

/** 单个粒子（池化对象，字段在 reset 时全部重写） */
class Particle {
  constructor() {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.life = 0;
    this.maxLife = 1;
    this.size = 2;
    this.color = '#ffffff';
    this.drag = 0.9;
    this.alive = false;
  }
}

export class ParticleSystem {
  /** @param {number} [maxParticles=500] 池容量上限 */
  constructor(maxParticles = 500) {
    this.maxParticles = maxParticles;
    /** @type {Particle[]} 空闲池 */
    this.pool = [];
    /** @type {Particle[]} 活跃粒子 */
    this.active = [];
  }

  /** 当前活跃粒子数（调试/性能观察用） */
  get count() {
    return this.active.length;
  }

  /* ===================================================
   * 生成
   * =================================================== */

  /**
   * 从一点向外爆发
   * @param {number} x
   * @param {number} y
   * @param {{count?:number,color?:string,speed?:number,life?:number,size?:number,drag?:number}} [opts]
   */
  spawnBurst(x, y, opts = {}) {
    const count = opts.count ?? 10;
    const color = opts.color ?? '#ffffff';
    const speed = opts.speed ?? 120;
    const life = opts.life ?? 0.5;
    const size = opts.size ?? 3;
    const drag = opts.drag ?? 0.9;

    for (let i = 0; i < count; i++) {
      if (this.active.length >= this.maxParticles) break;

      const p = this._acquire();
      const angle = Math.random() * Math.PI * 2;
      const spd = speed * (0.4 + Math.random() * 0.8);

      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * spd;
      p.vy = Math.sin(angle) * spd;
      p.maxLife = life * (0.6 + Math.random() * 0.6);
      p.life = p.maxLife;
      p.size = size * (0.7 + Math.random() * 0.6);
      p.color = color;
      p.drag = drag;
    }
  }

  /** 命中碎屑（少量、高速、短命） */
  spawnHit(x, y, color, count = 3) {
    this.spawnBurst(x, y, {
      count,
      color,
      speed: 170,
      life: 0.28,
      size: 2.2,
    });
  }

  /** 枪口火花（沿炮口方向锥形喷出） */
  spawnMuzzle(x, y, angle, color) {
    for (let i = 0; i < 4; i++) {
      if (this.active.length >= this.maxParticles) break;

      const p = this._acquire();
      const a = angle + (Math.random() - 0.5) * 0.8;
      const spd = 90 + Math.random() * 90;

      p.x = x;
      p.y = y;
      p.vx = Math.cos(a) * spd;
      p.vy = Math.sin(a) * spd;
      p.maxLife = 0.16 + Math.random() * 0.12;
      p.life = p.maxLife;
      p.size = 1.8 + Math.random() * 1.4;
      p.color = color;
      p.drag = 0.85;
    }
  }

  /* ===================================================
   * 更新 / 绘制
   * =================================================== */

  update(dt) {
    const active = this.active;
    let write = 0;

    for (let i = 0; i < active.length; i++) {
      const p = active[i];

      p.life -= dt;
      if (p.life <= 0) {
        this._release(p);
        continue;
      }

      p.x += p.vx * dt;
      p.y += p.vy * dt;

      // 帧率无关的阻尼
      const d = Math.pow(p.drag, dt * 60);
      p.vx *= d;
      p.vy *= d;

      active[write++] = p;
    }

    active.length = write;
  }

  draw(ctx) {
    const active = this.active;
    if (active.length === 0) return;

    ctx.save();
    for (let i = 0; i < active.length; i++) {
      const p = active[i];
      const t = p.maxLife > 0 ? p.life / p.maxLife : 0;

      ctx.globalAlpha = Math.max(0, Math.min(1, t));
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * (0.4 + t * 0.6), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  /** 清空所有粒子（重开游戏用） */
  clear() {
    for (let i = 0; i < this.active.length; i++) {
      this._release(this.active[i]);
    }
    this.active.length = 0;
  }

  /* ===================================================
   * 对象池
   * =================================================== */

  _acquire() {
    const p = this.pool.pop() || new Particle();
    p.alive = true;
    this.active.push(p);
    return p;
  }

  _release(p) {
    p.alive = false;
    if (this.pool.length < this.maxParticles) this.pool.push(p);
  }
}
